// ===============================================================
// 👥 FriendsPage.js — Friends / Followers / Following
// Header unified with TopBar (i18n + LIVORA)
// ✅ FIX: resolve avatars (PATH/gs:///downloadURL -> http)
// ✅ FIX: Follow/Unfollow Server-only via Cloud Functions
// ✅ FIX: notifications are only notifications, follows stay in follows
// ===============================================================

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { db, storage } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";

import TopBar from "../components/common/TopBar";
import BottomNav from "../components/BottomNav";
import "./FriendsPage.css";
import { defaultAvatarByGender } from "../utils/defaultAvatar";

// ===============================================================
// ✅ Image helpers
// ===============================================================
function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  if (!isHttpUrl(value) && !value.startsWith("gs://")) return value;
  if (value.startsWith("gs://")) return value;

  try {
    const part = value.split("/o/")[1];
    if (!part) return "";
    return decodeURIComponent((part.split("?")[0] || "").trim());
  } catch {
    return "";
  }
}

async function resolveToDownloadUrl(value) {
  if (!value) return "";

  if (isHttpUrl(value) && !value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  if (isHttpUrl(value) && value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  try {
    const pathOrGs = storagePathFromValue(value) || value;
    return await getDownloadURL(storageRef(storage, pathOrGs));
  } catch (e) {
    console.warn("resolveToDownloadUrl failed:", value, e);
    return "";
  }
}

// ===============================================================
// 🧩 Load users by IDs
// ===============================================================
async function loadUsersByIds(ids) {
  if (!ids || ids.length === 0) return [];

  const uniqueIds = [...new Set(ids)];

  const results = await Promise.all(
    uniqueIds.map(async (uid) => {
      try {
        const userRef = doc(db, "publicProfiles", uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) return null;

        const data = snap.data() || {};

        const clean = (v) =>
          typeof v === "string" && v.trim() !== "" ? v.trim() : null;

        const finalName =
          clean(data.username) ||
          clean(data.name) ||
          clean(data.displayName) ||
          "User";

        const rawPhoto =
          data.profileImage || data.photoURL || (data.photos?.[0] ?? "");

        const photoURL = rawPhoto
          ? (await resolveToDownloadUrl(rawPhoto)) || ""
          : "";

        return {
          uid,
          displayName: finalName,
          photoURL,
          gender: data.gender || "",
          isOnline: data.online || false,
          // Phase N9D: inCall is the server-authoritative Busy mirror (see
          // functions/calls/locks.js) - Busy takes priority over the green
          // online dot everywhere a user can be shown as live.
          isBusy: data.inCall === true,
        };
      } catch (e) {
        console.error("Load user error:", e);
        return null;
      }
    })
  );

  return results.filter(Boolean);
}

// ===============================================================
// 🔥 FriendsPage
// ===============================================================
function FriendsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isReady } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState("mutual");
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [busyUid, setBusyUid] = useState("");

  // =============================================================
  // Load followers / following
  // =============================================================
  useEffect(() => {
    if (!isReady) return;

    if (!user) {
      navigate("/");
      return;
    }

    const uid = user.uid;
    const followsRef = collection(db, "follows");

    const qFollowers = query(followsRef, where("toUid", "==", uid));
    const qFollowing = query(followsRef, where("fromUid", "==", uid));

    const unsubFollowers = onSnapshot(qFollowers, async (snap) => {
      const ids = snap.docs.map((d) => d.data().fromUid).filter(Boolean);
      setFollowers(await loadUsersByIds(ids));
    });

    const unsubFollowing = onSnapshot(qFollowing, async (snap) => {
      const ids = snap.docs.map((d) => d.data().toUid).filter(Boolean);
      setFollowing(await loadUsersByIds(ids));
    });

    return () => {
      unsubFollowers();
      unsubFollowing();
    };
  }, [isReady, user, navigate]);

  // =============================================================
  // Mutual users
  // =============================================================
  const followerIds = new Set(followers.map((u) => u.uid));
  const followingIds = new Set(following.map((u) => u.uid));
  const mutualUsers = followers.filter((u) => followingIds.has(u.uid));

  // =============================================================
  // ✅ Follow / Unfollow — Server-only
  // =============================================================
  const toggleFollow = async (targetUid, isFollowingNow) => {
    if (!isReady || !user || !targetUid || busyUid) return;

    try {
      setBusyUid(targetUid);

      const functions = getFunctions();

      if (isFollowingNow) {
        const unfollowUser = httpsCallable(functions, "unfollowUser");
        await unfollowUser({ targetUid });
      } else {
        const followUser = httpsCallable(functions, "followUser");
        await followUser({ targetUid });
      }
    } catch (e) {
      console.error("toggleFollow error:", e);
    } finally {
      setBusyUid("");
    }
  };

  // =============================================================
  // Render user row
  // =============================================================
  const renderUserRow = (user, type) => {
    const isFollowingUser = followingIds.has(user.uid);
    const isMutual = followerIds.has(user.uid) && isFollowingUser;
    const isBusy = busyUid === user.uid;

    let label = t("follow", { defaultValue: "متابعة" });

    if (type !== "followers") {
      label = t("unfollow", { defaultValue: "إلغاء المتابعة" });
    }

    if (type === "followers" && isMutual) {
      label = t("friend", { defaultValue: "صديق" });
    }

    return (
      <div
        key={user.uid}
        className="friend-item"
        onClick={() => navigate(`/user/${user.uid}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            navigate(`/user/${user.uid}`);
          }
        }}
      >
        <div className="friend-left">
          <div className="friend-avatar">
            <img
              src={user.photoURL || defaultAvatarByGender(user.gender)}
              alt=""
              onError={(e) => {
                e.currentTarget.src = defaultAvatarByGender(user.gender);
              }}
            />

            {(user.isOnline || user.isBusy) && (
              <span
                className={`friend-online-dot${user.isBusy ? " is-busy" : ""}`}
              />
            )}
          </div>

          <div className="friend-info">
            <div className="friend-name-row">
              <span className="friend-name">{user.displayName}</span>

              {isMutual && (
                <span className="friend-badge">
                  {t("mutual", { defaultValue: "مشترك" })}
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          className={
            "friend-btn " +
            (isFollowingUser
              ? "friend-btn-following"
              : "friend-btn-not-following")
          }
          disabled={isBusy}
          onClick={(e) => {
            e.stopPropagation();
            toggleFollow(user.uid, isFollowingUser);
          }}
        >
          {isBusy ? t("loading", { defaultValue: "..." }) : label}
        </button>
      </div>
    );
  };

  // =============================================================
  // Current list
  // =============================================================
  let list = [];

  if (activeTab === "mutual") list = mutualUsers;
  else if (activeTab === "followers") list = followers;
  else list = following;

  return (
    <div className="friends-root">
      <TopBar
        title={t("friends", { defaultValue: "الأصدقاء" })}
        onBack={() => navigate("/messages")}
      />

      <div className="friends-tabs">
        <button
          type="button"
          className={activeTab === "mutual" ? "f-tab active" : "f-tab"}
          onClick={() => setActiveTab("mutual")}
        >
          {t("friends", { defaultValue: "الأصدقاء" })}
        </button>

        <button
          type="button"
          className={activeTab === "followers" ? "f-tab active" : "f-tab"}
          onClick={() => setActiveTab("followers")}
        >
          {t("followers", { defaultValue: "المتابعون" })}
        </button>

        <button
          type="button"
          className={activeTab === "following" ? "f-tab active" : "f-tab"}
          onClick={() => setActiveTab("following")}
        >
          {t("following", { defaultValue: "أتابعهم" })}
        </button>
      </div>

      <div className="friends-list">
        {list.length === 0 ? (
          <p className="friends-empty">
            {t("no_data", { defaultValue: "لا توجد بيانات" })}
          </p>
        ) : (
          list.map((u) => renderUserRow(u, activeTab))
        )}
      </div>

      <BottomNav active="messages" />
    </div>
  );
}

export default FriendsPage;